import React, { useState } from "react";
import { Plus, Pencil, Trash2, Download, FileSignature, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, downloadPdf } from "@/lib/api";
import { PrimaryButton, GhostButton, EmptyState, Field, inputCls, formatCOP, StatusBadge } from "@/components/common";
import { DocTable, IconBtn, ClientSelect, LineItemsEditor, TotalsBox, itemsTotal, normalizeItems, ShareButtons } from "./shared";

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
const empty = () => ({ client_id: "", title: "", intro: "", issued_at: today(), valid_until: plusDays(15), items: [], discount: 0, notes: "" });
const STATUSES = ["borrador", "enviada", "aprobada", "rechazada"];

export default function CotizacionesTab({ quotes, clients, services, reload, onGoContracts }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editing, setEditing] = useState(null);
  const clientMap = Object.fromEntries(clients.map(c => [c.client_id, c]));

  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (q) => { setEditing(q.quote_id); setForm({ ...empty(), ...q }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.error("Selecciona un cliente"); return; }
    if (form.items.length === 0) { toast.error("Agrega al menos un ítem"); return; }
    const payload = { ...form, items: normalizeItems(form.items), discount: Number(form.discount) || 0 };
    try {
      if (editing) await api.put(`/quotes/${editing}`, payload);
      else await api.post("/quotes", payload);
      toast.success(editing ? "Cotización actualizada" : "Cotización creada");
      setOpen(false); reload();
    } catch { toast.error("No se pudo guardar la cotización"); }
  };

  const remove = async (q) => {
    if (!window.confirm(`¿Eliminar la cotización ${q.number}?`)) return;
    await api.delete(`/quotes/${q.quote_id}`);
    toast.success("Cotización eliminada"); reload();
  };

  const setStatus = async (q, status) => {
    await api.patch(`/quotes/${q.quote_id}/status`, { status });
    toast.success(`Estado: ${status}`); reload();
  };

  const convert = async (q) => {
    if (!window.confirm(`¿Convertir ${q.number} en contrato? La cotización quedará como aprobada.`)) return;
    try {
      const { data } = await api.post(`/quotes/${q.quote_id}/convert-to-contract`);
      toast.success(`Contrato ${data.number} creado`); reload(); onGoContracts?.();
    } catch { toast.error("No se pudo convertir"); }
  };

  const pdf = async (q) => {
    try { await downloadPdf(`/quotes/${q.quote_id}/pdf`, `${q.number}.pdf`); }
    catch { toast.error("No se pudo generar el PDF"); }
  };

  const subtotal = itemsTotal(form.items);

  return (
    <div data-testid="cotizaciones-tab">
      <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
        <p className="text-sm text-[#9CA3AF]">Propuestas comerciales para tus clientes. Al aprobarse, conviértelas en contrato con un clic.</p>
        <PrimaryButton data-testid="add-quote-btn" onClick={openNew}><Plus className="w-4 h-4"/> Nueva cotización</PrimaryButton>
      </div>
      {quotes.length === 0 ? (
        <EmptyState icon={FileText} title="Aún no tienes cotizaciones" description="Crea tu primera propuesta con los servicios de tu catálogo y descárgala en PDF con tu marca."
          action={<PrimaryButton onClick={openNew}><Plus className="w-4 h-4"/> Crear cotización</PrimaryButton>}/>
      ) : (
        <DocTable headers={["Número", "Cliente", "Título", "Fecha", { label: "Total", right: true }, "Estado", { label: "Acciones", right: true }]}>
          {quotes.map(q => (
            <tr key={q.quote_id} data-testid={`quote-row-${q.quote_id}`} className="border-b border-[#262A33] last:border-0 hover:bg-[#1A1D24] transition-colors">
              <td className="px-5 py-3 text-[#F5F5F7] font-mono text-xs">{q.number}</td>
              <td className="px-5 py-3 text-[#F5F5F7]">{clientMap[q.client_id]?.name || "—"}</td>
              <td className="px-5 py-3 text-[#9CA3AF] max-w-[220px] truncate">{q.title || "—"}</td>
              <td className="px-5 py-3 text-[#9CA3AF]">{q.issued_at}</td>
              <td className="px-5 py-3 text-right text-[#F5F5F7] font-medium">{formatCOP(q.total)}</td>
              <td className="px-5 py-3">
                <select data-testid={`quote-status-${q.quote_id}`} value={q.status} onChange={e => setStatus(q, e.target.value)}
                  className="bg-transparent text-xs text-[#F5F5F7] border border-[#262A33] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#F97316]">
                  {STATUSES.map(s => <option key={s} value={s} className="bg-[#15171E]">{s}</option>)}
                </select>
              </td>
              <td className="px-5 py-3 text-right">
                <div className="inline-flex gap-1">
                  <IconBtn data-testid={`quote-pdf-${q.quote_id}`} onClick={() => pdf(q)} className="text-[#F97316] hover:bg-[#F97316]/10" title="Descargar PDF" aria-label="PDF"><Download className="w-4 h-4"/></IconBtn>
                  <ShareButtons path={`/quotes/${q.quote_id}`} clientEmail={clientMap[q.client_id]?.email ?? ""} testId={`quote-${q.quote_id}`} onSent={reload}/>
                  {q.contract_id ? (
                    <IconBtn onClick={onGoContracts} className="text-emerald-400 hover:bg-emerald-500/10" title="Ver contrato" aria-label="Contrato"><FileSignature className="w-4 h-4"/></IconBtn>
                  ) : (
                    <IconBtn data-testid={`quote-convert-${q.quote_id}`} onClick={() => convert(q)} className="text-[#9CA3AF] hover:text-emerald-400 hover:bg-emerald-500/10" title="Convertir en contrato" aria-label="Convertir"><FileSignature className="w-4 h-4"/></IconBtn>
                  )}
                  <IconBtn data-testid={`quote-edit-${q.quote_id}`} onClick={() => openEdit(q)} className="text-[#9CA3AF] hover:text-[#F5F5F7] hover:bg-[#262A33]" aria-label="Editar"><Pencil className="w-4 h-4"/></IconBtn>
                  <IconBtn onClick={() => remove(q)} className="text-[#6B7280] hover:text-red-400 hover:bg-red-500/10" aria-label="Eliminar"><Trash2 className="w-4 h-4"/></IconBtn>
                </div>
              </td>
            </tr>
          ))}
        </DocTable>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#15171E] border-[#262A33] text-[#F5F5F7] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>{editing ? "Editar cotización" : "Nueva cotización"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Cliente *"><ClientSelect clients={clients} value={form.client_id} onChange={v => setForm({ ...form, client_id: v })} testId="quote-client-select"/></Field>
              <Field label="Título de la propuesta">
                <input data-testid="quote-title-input" className={inputCls} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ej. Servicio manejo de redes sociales"/>
              </Field>
            </div>
            <Field label="Introducción" hint="Texto breve que aparece antes de la tabla de servicios.">
              <textarea rows={2} className={inputCls} value={form.intro} onChange={e => setForm({ ...form, intro: e.target.value })} placeholder="Presentamos nuestra propuesta de servicios de marketing digital para…"/>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Fecha"><input type="date" className={inputCls} value={form.issued_at} onChange={e => setForm({ ...form, issued_at: e.target.value })}/></Field>
              <Field label="Válida hasta"><input type="date" className={inputCls} value={form.valid_until || ""} onChange={e => setForm({ ...form, valid_until: e.target.value })}/></Field>
            </div>
            <LineItemsEditor items={form.items} onChange={items => setForm({ ...form, items })} services={services} testPrefix="quote-item"/>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
              <Field label="Descuento (COP)">
                <input data-testid="quote-discount-input" type="number" min="0" className={inputCls} value={form.discount} onChange={e => setForm({ ...form, discount: e.target.value })}/>
              </Field>
              <TotalsBox subtotal={subtotal} discount={Number(form.discount) || 0}/>
            </div>
            <Field label="Condiciones" hint="Si lo dejas vacío se usan las condiciones por defecto de Configuración.">
              <textarea rows={3} className={inputCls} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}/>
            </Field>
            <DialogFooter>
              <GhostButton type="button" onClick={() => setOpen(false)}>Cancelar</GhostButton>
              <PrimaryButton data-testid="save-quote-btn" type="submit">{editing ? "Guardar" : "Crear cotización"}</PrimaryButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
